import type React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppIcons } from '../config/AppIcons';
import { useI18n } from '../i18n';
import type { MobileWorkspace } from '../utils/mobileWorkspace';

interface MobileShellNavigationProps {
  hasTeam: boolean;
  isFeedbackOpen: boolean;
  onCloseFeedback: () => void;
  onOpenFeedback: () => void;
  workspace: MobileWorkspace;
}

type NavigationItem = {
  to: string;
  labelKey: string;
  icon: typeof AppIcons.dashboard;
  end?: boolean;
  requiresTeam?: boolean;
  activePrefixes?: string[];
};

const coachNavigationItems: NavigationItem[] = [
  { to: '/', labelKey: 'mobile_nav_dugout', icon: AppIcons.dashboard, end: true },
  { to: '/match', labelKey: 'mobile_nav_match', icon: AppIcons.matches },
  { to: '/team-management', labelKey: 'mobile_nav_team', icon: AppIcons.team, requiresTeam: true, activePrefixes: ['/player-stats'] },
];

const trainerNavigationItems: NavigationItem[] = [
  { to: '/trainer-dugout', labelKey: 'mobile_nav_dugout', icon: AppIcons.dashboard, end: true },
  { to: '/training-practices', labelKey: 'mobile_nav_exercises', icon: AppIcons.training_exercises },
  { to: '/training-sessions', labelKey: 'mobile_nav_sessions', icon: AppIcons.training_sessions },
  { to: '/training-exercise-plans', labelKey: 'mobile_nav_plans', icon: AppIcons.training_exercise_plans, activePrefixes: ['/plans/'] },
];

const MobileShellNavigation: React.FC<MobileShellNavigationProps> = ({ hasTeam, isFeedbackOpen, onCloseFeedback, onOpenFeedback, workspace }) => {
  const { t } = useI18n();
  const location = useLocation();
  const navigationItems = workspace === 'trainer' ? trainerNavigationItems : coachNavigationItems;
  const navigationHeight = 'h-16';
  const itemTypography = 'gap-1 text-xs';
  const iconSize = 22;
  const navigationColumns = workspace === 'trainer' ? 'grid-cols-5' : 'grid-cols-4';

  return (
    <nav
      aria-label={t(workspace === 'trainer' ? 'mobile_nav_trainer_label' : 'mobile_nav_label')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur min-[900px]:hidden"
    >
      <div className={`mx-auto grid max-w-lg px-1 ${navigationColumns} ${navigationHeight}`}>
        {navigationItems.map(({ to, labelKey, icon: Icon, end, requiresTeam, activePrefixes }) => {
          const disabled = Boolean(requiresTeam && !hasTeam);
          const isActive = location.pathname === to || (!end && location.pathname.startsWith(`${to}/`));
          const matchesAlternatePath = Boolean(activePrefixes?.some((prefix) => location.pathname.startsWith(prefix)));

          if (disabled) {
            return (
              <span
                key={to}
                aria-disabled="true"
                className={`flex min-w-0 flex-col items-center justify-center px-1 font-medium leading-none text-gray-300 ${itemTypography}`}
              >
                <Icon size={iconSize} aria-hidden="true" />
                <span className="truncate">{t(labelKey)}</span>
              </span>
            );
          }

          return (
            <Link
              key={to}
              to={to}
              onClick={onCloseFeedback}
              aria-current={isActive || matchesAlternatePath ? 'page' : undefined}
              className={`flex min-w-0 flex-col items-center justify-center px-1 font-medium leading-none transition-colors ${itemTypography} ${
                isActive || matchesAlternatePath ? 'text-primary-700' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {workspace === 'coach' && to === '/' ? (
                <span
                  className="inline-block h-[22px] w-[22px] bg-current"
                  style={{
                    maskImage: "url('/icons/ferraro_bench.svg')",
                    WebkitMaskImage: "url('/icons/ferraro_bench.svg')",
                    maskPosition: 'center',
                    WebkitMaskPosition: 'center',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                  }}
                  aria-hidden="true"
                  data-active={isActive || matchesAlternatePath ? 'true' : 'false'}
                />
              ) : (
                <Icon size={iconSize} aria-hidden="true" />
              )}
              <span className="truncate">{t(labelKey)}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onOpenFeedback}
          aria-pressed={isFeedbackOpen}
          className={`flex min-w-0 flex-col items-center justify-center px-1 font-medium leading-none transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400 ${itemTypography} ${
            isFeedbackOpen ? 'text-primary-700' : 'text-gray-500'
          }`}
        >
          <AppIcons.support size={iconSize} aria-hidden="true" />
          <span className="truncate">{t('mobile_nav_feedback')}</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileShellNavigation;
